import { useGetJourneyBugsQuery } from '../services/projectDataApi'
import { Route as RouteIcon, X, Bug, ExternalLink } from 'lucide-react'
import Spinner from './Spinner'
import type { Journey } from '../data/projectTypes'
import { isSurfacedFinding } from '../../netlify/functions/lib/bug-statuses'
import { Link } from './nav'
import TaskEventReplay from './TaskEventReplay'
import { taskDetailUrl } from '../lib/taskDetailPath'
import './OverviewJourneyPlayer.css'

const CATEGORY_LABELS: Record<string, string> = {
  'layout-shift': 'layout shift',
  'network-performance': 'performance',
  'react-rendering': 'react rendering',
  accessibility: 'accessibility',
  seo: 'SEO',
}

// The expanded view for a journey selected on the (experimental) project overview. Clicking a
// journey card swaps the card for this panel in place: an rrweb replay of the journey's latest run
// (mirroring the exploration player), the list of bugs the journey filed below it, and a close
// button in the header to dismiss back to the card. A pure consumer — ProjectOverview owns the
// selection state; this fetches its own bug list on mount.
export default function OverviewJourneyPlayer({ journey, projectId, onClose }: { journey: Journey; projectId: string; onClose: () => void }) {
  const { data: bugs, isLoading: bugsLoading } = useGetJourneyBugsQuery(journey.id)

  const runId = journey.preview_run_id ?? journey.last_run_id ?? null
  const running = journey.last_run_status === 'in-progress' || journey.last_run_status === 'queued'
  // Filed bugs still worth showing — drop dismissed (wontfix / invalid) ones so the list
  // reflects what QA actually reported for the journey.
  const visibleBugs = (bugs ?? []).filter(b => isSurfacedFinding(b.status))

  return (
    <section className="ov-journey" data-testid={`overview-journey-player-${journey.id}`}>
      <div className="ov-journey__head">
        <span className="ov-journey__title">
          <RouteIcon size={16} strokeWidth={1.75} aria-hidden /> {journey.name}
          {running && <Spinner size={13} className="ov-journey__spin" />}
        </span>
        <button
          type="button"
          className="ov-journey__close"
          onClick={onClose}
          aria-label="Close journey"
          data-testid={`overview-journey-close-${journey.id}`}
          data-analytics-event="new-overview-journey-close"
        >
          <X size={16} aria-hidden />
        </button>
      </div>

      <div className="ov-journey__stage">
        {runId ? (
          <TaskEventReplay
            key={runId}
            source={{ testRunId: runId }}
            taskHref={taskDetailUrl(projectId, runId)}
            active
            live={running}
          />
        ) : (
          <p className="ov-journey__no-replay" data-testid={`overview-journey-no-replay-${journey.id}`}>
            No test runs yet — nothing to replay.
          </p>
        )}
      </div>

      <div className="ov-journey__bugs" data-testid={`overview-journey-bugs-${journey.id}`}>
        <h3 className="ov-journey__bugs-title">
          <Bug size={14} aria-hidden /> Bugs filed
          {bugs ? ` (${visibleBugs.length})` : ''}
        </h3>
        {bugsLoading ? (
          <p className="ov-journey__bugs-empty">Loading bugs…</p>
        ) : visibleBugs.length === 0 ? (
          <p className="ov-journey__bugs-empty" data-testid={`overview-journey-bugs-empty-${journey.id}`}>
            No bugs filed by this journey.
          </p>
        ) : (
          <div className="ov-journey__bug-list">
            {visibleBugs.map(b => (
              <Link
                key={b.id}
                to={`/projects/${projectId}/bugs/${b.id}`}
                className="ov-journey__bug-row"
                data-testid={`overview-journey-bug-${b.id}`}
                data-analytics-event="new-overview-journey-bug-link"
              >
                <span className={`ov-journey__bug-severity ov-journey__bug-severity--${b.severity}`}>
                  {b.severity}
                </span>
                <span className="ov-journey__bug-title">{b.title}</span>
                <span className="ov-journey__bug-kind">
                  {b.polish_category ? (CATEGORY_LABELS[b.polish_category] ?? b.polish_category) : 'testing'}
                </span>
                <ExternalLink size={12} className="ov-journey__bug-arrow" aria-hidden />
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
